import type { FC } from "react";
import { useEffect, useRef, useState } from "react";
import { useWebsiteKeywordsPageTableTopContext } from "app/pages/website-analysis/traffic-sources/search/components/WebsiteKeywordsPageContext";
import { FILTER_DEFAULT_SEPARATOR } from "app/pages/website-analysis/constants/constants";
import { ChipDownContainer, EllipsisDropdownItem } from "@similarweb/ui-components";
import { FooterButton } from "pro-features/components/MultiCategoriesChipDown/src/MultiCategoryChipdownStyles";
import type { IWebsiteKeywordsPageFilters } from "../WebsiteKeywordsPageTypes";
import type { SearchEngine } from "app/pages/website-analysis/constants/searchEngines";
import allSearchEngines from "app/pages/website-analysis/constants/searchEngines";
import { abbrNumberVisitsFilter } from "app/filters/ngFilters";
import { getDropdownTitle } from "pro-features/components/MultiChipDown/src/MultiChipDown";
import { i18nFilter } from "app/filters/i18nFilter";
import { allTrackers } from "app/services/track/track";

interface ISearchEnginesFilterProps {
    tableFilters: IWebsiteKeywordsPageFilters;
    onSearchEngineChange: (selected: string[]) => void;
    withCount: boolean;
    searchEngines: SearchEngine[];
    searchEngineList?: SearchEngine[];
}

const getSelectedSearchEngineFromList = (searchEnginesFullList: SearchEngine[], selectedList: string[]): string[] => {
    return selectedList.filter((id) => searchEnginesFullList?.some((item) => item.id === id));
};
// This is the representational component
const SearchEnginesFilter: FC<ISearchEnginesFilterProps> = ({
    tableFilters,
    onSearchEngineChange,
    withCount,
    searchEngines = [],
    searchEngineList = null,
}) => {
    const initialSelected = useRef(tableFilters.family?.split(FILTER_DEFAULT_SEPARATOR) ?? []);
    const dropdownRef = useRef<{ closePopup?: VoidFunction }>(null);
    const searchEnginesList: SearchEngine[] = searchEngineList ?? allSearchEngines;

    const [selected, setSelected] = useState(
        getSelectedSearchEngineFromList(searchEnginesList, initialSelected.current),
    );

    useEffect(() => {
        if (tableFilters.family) {
            initialSelected.current = tableFilters.family.split(FILTER_DEFAULT_SEPARATOR);
            setSelected(getSelectedSearchEngineFromList(searchEnginesList, initialSelected.current));
        } else {
            setSelected([]);
            initialSelected.current = [];
        }
    }, [tableFilters.family]);

    const onToggle = (isOpen: boolean) => {
        if (isOpen) {
            allTrackers.dispatchTrackEvent("Drop Down", "open", "open Search Type filter");
        } else {
            onSearchEngineChange(selected ?? []);
        }
    };

    const onClick = ({ id }) => {
        const index = selected.indexOf(id);
        const newSelected = [...selected];
        if (index > -1) {
            newSelected.splice(index, 1);
            allTrackers.dispatchTrackEvent("Drop Down", "click", `deselect Search Type/${id || ""}`);
        } else {
            newSelected.push(id);
            allTrackers.dispatchTrackEvent("Drop Down", "click", `select Search Type/${id || ""}`);
        }
        setSelected(newSelected);
    };

    const selectedIds = selected.reduce((result, id) => {
        result[id] = true;
        return result;
    }, {});

    const selectedItemsTitles = selected.map((id) => searchEnginesList.find((type) => type.id === id)?.text);
    const allItems = searchEnginesList
        .map((type) => {
            const searchTypeCount = searchEngines.find((engine) => engine.id.toString() === type.id)?.count;
            return {
                ...type,
                count: searchTypeCount ?? 0,
            };
        })
        .sort((a, b) => b.count - a.count);

    const onClose = () => {
        dropdownRef?.current?.closePopup();
        onSearchEngineChange([]);
    };
    const onApply = () => {
        dropdownRef?.current?.closePopup();
        onSearchEngineChange(selected);

        allTrackers.dispatchTrackEvent("Button", "submit-ok", `Apply Search Type Filter/${selected.join(", ") || ""}`);
    };

    const allItemsElements = allItems.map((type) => {
        return (
            <EllipsisDropdownItem
                key={type.id}
                id={type.id}
                showCheckBox={true}
                selected={selected.includes(type.id)}
                infoText={withCount ? abbrNumberVisitsFilter()(type?.count || 0) : undefined}
                disabled={withCount && type.count === 0}
            >
                {type.text}
            </EllipsisDropdownItem>
        );
    });
    return (
        <div>
            <ChipDownContainer
                ref={dropdownRef}
                onToggle={onToggle}
                hasSearch={false}
                selectedIds={selectedIds}
                selectedText={getDropdownTitle(selectedItemsTitles)}
                buttonText={i18nFilter()("analysis.source.search.keywords.filters.all-sources")}
                onClick={onClick}
                onCloseItem={onClose}
                closeOnItemClick={false}
                width={300}
                noResultsMessage={i18nFilter()("dropdown.no.results")}
                footerComponent={() => (
                    <FooterButton onClick={onApply}>{i18nFilter()("serp.filter.apply")}</FooterButton>
                )}
            >
                {allItemsElements}
            </ChipDownContainer>
        </div>
    );
};
// This are the "connectors" that connect the certain context to the representational component

export const SearchEnginesFilterForWebsiteKeywords = ({ withCount = true, searchEngineList = null }) => {
    const { tableFilters, searchEngines, onSearchEngineChange } = useWebsiteKeywordsPageTableTopContext();
    return (
        <SearchEnginesFilter
            tableFilters={tableFilters}
            searchEngines={searchEngines}
            onSearchEngineChange={onSearchEngineChange}
            withCount={withCount}
            searchEngineList={searchEngineList}
        />
    );
};
